import { HttpErrorResponse } from '@angular/common/http';
import * as Sentry from '@sentry/angular';

/**
 * Throttling for noisy runtime errors (e.g. "Cannot read properties of
 * undefined") before they reach Sentry. A broken render can fire the same error
 * dozens of times per second across many users, so we report each distinct
 * error signature at most once per cooldown window, with a global per-session
 * cap as a hard safety net.
 */

/** Same error signature is reported at most once within this window. */
const SIGNATURE_COOLDOWN_MS = 5 * 60 * 1000;
/** Hard cap on the number of runtime errors reported per page session. */
const MAX_EVENTS_PER_SESSION = 50;

const lastSentBySignature = new Map<string, number>();
let sessionEventCount = 0;

/**
 * Builds a stable key for an error so repeats collapse while distinct bugs stay
 * apart: error name + message + the first stack frame (the throw site).
 */
function errorSignature(error: unknown): string {
  if (error instanceof Error) {
    const firstFrame = error.stack?.split('\n')[1]?.trim() ?? '';
    return `${error.name}:${error.message}:${firstFrame}`;
  }
  return String(error);
}

/**
 * Reports a runtime error to Sentry, throttled by signature and capped per
 * session. HTTP errors are ignored here — they are reported with full request
 * context by the HTTP interceptor. Returns `true` when the error was sent.
 */
export function reportRuntimeErrorToSentry(error: unknown): boolean {
  // Avoid double-reporting: HTTP failures are handled by the HTTP interceptor.
  if (error instanceof HttpErrorResponse) return false;

  if (sessionEventCount >= MAX_EVENTS_PER_SESSION) return false;

  const signature = errorSignature(error);
  const now = Date.now();
  const lastSent = lastSentBySignature.get(signature);
  if (lastSent !== undefined && now - lastSent < SIGNATURE_COOLDOWN_MS) {
    return false;
  }

  lastSentBySignature.set(signature, now);
  sessionEventCount += 1;

  Sentry.withScope((scope) => {
    scope.setTag('error_type', 'runtime');
    Sentry.captureException(error);
  });
  return true;
}
